import { GasProductionDataType, GasStorageDataType, GasStorageVisualization } from '@e2m-customer-portal/domains';
import { Nullable } from '@e2m-customer-portal/shared';
import { IconButton } from '@mui/material';
import { BiogasSystemIcon, EditIcon } from 'assets/icons';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GasStorageSidePanel from './GasStorageSidePanel';

interface OptimizationGasStorageProps {
  gasStorageData: Nullable<GasStorageDataType>;
  gasProductionData: Nullable<GasProductionDataType>;
}

export function OptimizationGasStorage({
  gasStorageData,
  gasProductionData,
}: Readonly<OptimizationGasStorageProps>): ReactElement<OptimizationGasStorageProps> {
  const [t] = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (!gasStorageData || !gasProductionData) return <>🤷</>;

  return (
    <div className="flex flex-col gap-y-3 w-64 pt-2 pb-4 px-5 box-border border-solid border border-blue-grey-100 rounded-lg">
      <GasStorageSidePanel
        open={isOpen}
        onClose={() => setIsOpen(false)}
        gasStorageData={gasStorageData}
        gasProductionData={gasProductionData}
      />
      <div className="flex justify-between items-center">
        <div className="flex items-end gap-x-2">
          <BiogasSystemIcon className="size-6" />
          <h3 className="m-0 font-bold text-sm">{t('OPTIMIZATION_GAS_STORAGE')}</h3>
        </div>
        <IconButton className="-m-1 p-1 disabled:opacity-25 disabled:grayscale">
          <EditIcon
            className="text-green-200"
            onClick={(event) => {
              setIsOpen(true);
              event.stopPropagation();
            }}
          />
        </IconButton>
      </div>
      <div className="text-xs">
        {gasStorageData.volume_capacity && (
          <div>
            {`${t('OPTIMIZATION_GAS_STORAGE_VOLUME_CAPACITY')}: `}
            <strong>{t('OPTIMIZATION_VALUE_CUBIC_METERS', { value: gasStorageData.volume_capacity })}</strong>
          </div>
        )}
        {gasStorageData.volume_capacity && gasStorageData.upper_soc_limit && (
          <div>
            {`${t('OPTIMIZATION_GAS_STORAGE_UPPER_SOC_LIMIT')}: `}
            <strong>
              {t('OPTIMIZATION_VALUE_CUBIC_METERS', {
                value: Math.round(gasStorageData.volume_capacity * gasStorageData.upper_soc_limit),
              })}
            </strong>
          </div>
        )}
        {gasStorageData.volume_capacity && gasStorageData.lower_soc_limit && (
          <div>
            {`${t('OPTIMIZATION_GAS_STORAGE_LOWER_SOC_LIMIT')}: `}
            <strong>
              {t('OPTIMIZATION_VALUE_CUBIC_METERS', {
                value: Math.round(gasStorageData.volume_capacity * gasStorageData.lower_soc_limit),
              })}
            </strong>
          </div>
        )}
      </div>
      <GasStorageVisualization data={gasStorageData} motionless />
    </div>
  );
}
